import { isTaskOverdue, isTerminalStatus } from '@agentic-flow-studio/shared/lib/task-list';
import type { RouterOutputs } from '@agentic-flow-studio/shared/lib/trpc';

export type DashboardTask = RouterOutputs['tasks']['getAll'][number];
type DashboardContact = RouterOutputs['contacts']['getAll'][number];
type TaskStatus = RouterOutputs['taskStatuses']['list']['items'][number];

export type DashboardActionItem = {
  task: DashboardTask;
  reason: string;
  tone: 'danger' | 'warning' | 'neutral';
};

export type ContactAttentionItem = {
  contact: DashboardContact;
  reason: string;
  overdueCount: number;
};

const DAY_SECONDS = 86_400;
const STALE_CONTACT_SECONDS = 30 * DAY_SECONDS;

export function buildDashboardActions({
  tasks,
  contacts,
  statuses,
  currentUserId,
  nowSec,
  startOfTodaySec,
}: {
  tasks: DashboardTask[];
  contacts: DashboardContact[];
  statuses: TaskStatus[];
  currentUserId: number | undefined;
  nowSec: number;
  startOfTodaySec: number;
}) {
  const endOfTodaySec = startOfTodaySec + DAY_SECONDS;
  const unfinishedTasks = tasks.filter((task) => !isTerminalStatus(statuses, task.status));
  const myOverdueTasks = unfinishedTasks.filter(
    (task) => task.assignedTo === currentUserId && isTaskOverdue(statuses, task, nowSec)
  );
  const dueTodayTasks = unfinishedTasks.filter(
    (task) => task.dueAt && task.dueAt >= startOfTodaySec && task.dueAt < endOfTodaySec
  );
  const unassignedTasks = unfinishedTasks.filter((task) => task.assignedTo == null);

  const overdueByContact = new Map<number, number>();
  for (const task of unfinishedTasks) {
    if (task.contactId && isTaskOverdue(statuses, task, nowSec)) {
      overdueByContact.set(task.contactId, (overdueByContact.get(task.contactId) ?? 0) + 1);
    }
  }

  const contactsNeedingAttention: ContactAttentionItem[] = contacts
    .map((contact) => {
      const overdueCount = overdueByContact.get(contact.id) ?? 0;
      const lacksContext = !contact.anchors?.length;
      const stale = contact.updatedAt < nowSec - STALE_CONTACT_SECONDS;
      const reason =
        overdueCount > 0
          ? `${overdueCount} overdue ${overdueCount === 1 ? 'task' : 'tasks'}`
          : lacksContext
            ? 'No anchor connected'
            : stale
              ? 'Not updated in 30+ days'
              : null;
      return { contact, reason, overdueCount };
    })
    .filter((item): item is ContactAttentionItem => item.reason !== null)
    .sort((a, b) => b.overdueCount - a.overdueCount || a.contact.updatedAt - b.contact.updatedAt);

  const seenTaskIds = new Set<number>();
  const nextActions: DashboardActionItem[] = [];
  const appendTasks = (
    source: DashboardTask[],
    reason: string,
    tone: DashboardActionItem['tone']
  ) => {
    for (const task of source) {
      if (seenTaskIds.has(task.id)) continue;
      seenTaskIds.add(task.id);
      nextActions.push({ task, reason, tone });
    }
  };
  appendTasks(myOverdueTasks, 'Overdue and assigned to you', 'danger');
  appendTasks(dueTodayTasks, 'Due today', 'warning');
  appendTasks(unassignedTasks, 'Needs an owner', 'neutral');

  return {
    myOverdueTasks,
    dueTodayTasks,
    unassignedTasks,
    contactsNeedingAttention,
    nextActions,
  };
}
